<script setup lang="ts">
import { NuxtLink } from '#components'
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Image gallery in two modes. Carousel is CSS scroll-snap — no library and no
 * lifecycle hook, so it renders identically on the server and still scrolls by
 * touch, trackpad and keyboard when JavaScript never runs. The arrow buttons
 * are the only part that needs JS, and they are progressive enhancement.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings, editing?: boolean }>()

const images = computed(() => props.data.images ?? [])
const isCarousel = computed(() => props.data.mode === 'carousel')

const isExternal = (url?: string | null) => !!url && /^(https?:|mailto:|tel:)/.test(url)

const gridClass = computed(() => ({
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4'
}[props.data.columns ?? 3] ?? 'sm:grid-cols-2 lg:grid-cols-3'))

const scroller = ref<HTMLElement | null>(null)

const nudge = (direction: 1 | -1) => {
  const el = scroller.value
  if (!el) return
  el.scrollBy({ left: direction * el.clientWidth * 0.8 })
}
</script>

<template>
  <div>
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      class="mb-6"
    />

    <!-- Carousel -->
    <div v-if="images.length && isCarousel">
      <div
        ref="scroller"
        tabindex="0"
        role="region"
        :aria-label="data.headline || 'Image gallery'"
        class="site-scroll-x flex snap-x snap-mandatory gap-4 pb-2"
      >
        <figure
          v-for="(img, i) in images"
          :key="i"
          class="shrink-0 basis-[82%] snap-center sm:basis-[46%] lg:basis-[32%]"
        >
          <component
            :is="img.link_url ? NuxtLink : 'div'"
            v-bind="img.link_url ? { to: img.link_url, external: isExternal(img.link_url) } : {}"
            class="block"
          >
            <img
              :src="img.url"
              :alt="img.alt || ''"
              class="aspect-[4/3] w-full rounded-[var(--site-radius)] object-cover"
              loading="lazy"
            >
          </component>
          <figcaption
            v-if="img.caption"
            class="mt-2 text-center text-sm text-[var(--site-muted)]"
          >
            {{ img.caption }}
          </figcaption>
        </figure>
      </div>

      <div
        v-if="images.length > 1"
        class="mt-3 flex justify-center gap-2"
      >
        <button
          type="button"
          aria-label="Previous images"
          class="site-btn-outline flex size-9 items-center justify-center"
          @click="nudge(-1)"
        >
          <svg
            class="size-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="m15 18-6-6 6-6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Next images"
          class="site-btn-outline flex size-9 items-center justify-center"
          @click="nudge(1)"
        >
          <svg
            class="size-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="m9 18 6-6-6-6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Grid -->
    <div
      v-else-if="images.length"
      class="grid grid-cols-2 gap-3"
      :class="gridClass"
    >
      <figure
        v-for="(img, i) in images"
        :key="i"
      >
        <component
          :is="img.link_url ? NuxtLink : 'div'"
          v-bind="img.link_url ? { to: img.link_url, external: isExternal(img.link_url) } : {}"
          class="block"
        >
          <img
            :src="img.url"
            :alt="img.alt || ''"
            class="aspect-[4/3] w-full rounded-[var(--site-radius)] object-cover"
            loading="lazy"
          >
        </component>
        <figcaption
          v-if="img.caption"
          class="mt-2 text-center text-sm text-[var(--site-muted)]"
        >
          {{ img.caption }}
        </figcaption>
      </figure>
    </div>

    <!-- No images yet: silent on the public page (as before), a click
         target while building — see BlockFrame's `editing` doc comment. -->
    <div
      v-else-if="editing"
      class="flex flex-col items-center gap-2 rounded-[var(--site-radius)] border border-dashed border-[var(--site-border)] p-10 text-center text-sm text-[var(--site-muted)]"
    >
      <svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </svg>
      <span>Add images on the right to fill this gallery in.</span>
    </div>
  </div>
</template>
